/**
 * DeviceDetector - Utility class for detecting device type and providing scaling factors
 */

import { Logger } from './Logger';

const logger = Logger.getLogger('DeviceDetector');

export class DeviceDetector {
  /**
   * Check if the current device is a mobile device
   */
  static isMobile(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUA = mobileRegex.test(userAgent);
    const isSmallScreen = window.innerWidth <= 768;

    return isMobileUA && isSmallScreen;
  }

  /**
   * Check if the device is a tablet (iPad, Android tablets)
   */
  static isTablet(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isTabletUA = /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
    const isMediumScreen = window.innerWidth > 768 && window.innerWidth <= 1024;

    return isTabletUA || isMediumScreen;
  }

  /**
   * Check if the device is a small mobile phone (iPhone SE, small Android phones)
   */
  static isSmallMobile(): boolean {
    return this.isMobile() && window.innerWidth <= 480;
  }

  /**
   * Get the appropriate UI scaling factor based on device type
   * Returns a multiplier for UI element sizes
   */
  static getUIScale(): number {
    if (this.isSmallMobile()) {
      return 0.7; // 70% size for small phones
    } else if (this.isMobile()) {
      return 0.8; // 80% size for regular mobile
    } else if (this.isTablet()) {
      return 0.9; // 90% size for tablets
    }
    return 1.0; // 100% size for desktop
  }

  /**
   * Get device category as a string
   */
  static getDeviceType(): 'small-mobile' | 'mobile' | 'tablet' | 'desktop' {
    if (this.isSmallMobile()) return 'small-mobile';
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    return 'desktop';
  }

  /**
   * Check if the device is running iOS (iPhone, iPad, iPod)
   */
  static isIOS(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    // Check for iOS devices
    const isIOSUA = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    // Also check for iPad on iOS 13+ which identifies as Mac
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isIOSUA || isIPadOS;
  }

  /**
   * Check if the device supports touch input
   */
  static isTouchDevice(): boolean {
    return ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0) ||
           ((navigator.msMaxTouchPoints ?? 0) > 0);
  }

  /**
   * Get the recommended VR GUI plane scale factor for mobile VR
   * When in VR mode on mobile (e.g., Google Cardboard), UI should be slightly smaller
   */
  static getVRGUIScale(): number {
    if (this.isMobile()) {
      return 0.85; // 85% size for mobile VR
    }
    return 1.0; // 100% for desktop VR
  }

  /**
   * Get viewport dimensions
   */
  static getViewportSize(): { width: number; height: number } {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  }

  /**
   * Detect if currently in VR mode
   * Checks for WebXR session or fullscreen mobile
   */
  static isInVR(): boolean {
    // Check if there's an active XR session (will be set by XRHelper)
    const xrHelper = window.xrHelper;
    if (xrHelper && xrHelper.inXR) {
      return true;
    }

    // Fallback: Check for fullscreen on mobile (Cardboard mode)
    return document.fullscreenElement !== null && this.isMobile();
  }

  /**
   * Get optimal texture variant key for current device
   * Returns the best variant to load based on device capabilities
   */
  static getTextureVariant(): 'desktop' | 'mobile' | 'tablet' | 'vr' {
    // VR has highest priority
    if (this.isInVR()) {
      return 'vr';
    }

    // Then check device type
    if (this.isSmallMobile()) {
      return 'mobile';
    }

    if (this.isMobile()) {
      return 'mobile';
    }

    if (this.isTablet()) {
      return 'tablet';
    }

    return 'desktop';
  }

  /**
   * Check if device supports KTX2 textures
   * Note: Babylon.js transcodes KTX2 to native GPU formats, so this is always true
   */
  static supportsKTX2(): boolean {
    // Babylon.js 8.x+ has built-in KTX2 transcoder that works on all browsers
    return true;
  }

  /**
   * Log device information (useful for debugging)
   */
  static logDeviceInfo(): void {
    logger.info('Device Information:', {
      type: this.getDeviceType(),
      isMobile: this.isMobile(),
      isTablet: this.isTablet(),
      isSmallMobile: this.isSmallMobile(),
      isIOS: this.isIOS(),
      isInVR: this.isInVR(),
      isTouchDevice: this.isTouchDevice(),
      textureVariant: this.getTextureVariant(),
      supportsKTX2: this.supportsKTX2(),
      uiScale: this.getUIScale(),
      vrGuiScale: this.getVRGUIScale(),
      viewport: this.getViewportSize(),
      userAgent: navigator.userAgent
    });
  }
}
